/**
 * Create by Marcus Zhao
 * Description: include Company,Job Title
 * ------ maintenance history ------
 * 2020/4/12 Bowen: edit relationship and employment part
 * 2020/9/11 Bowen: sync the job title
 */

import { Component, OnInit, Input, ViewChild, OnDestroy } from '@angular/core';
import { ReplaySubject, Subject, Subscription } from 'rxjs';
import { AppConfig } from '@tamalelibs/models/app-config.model';
import { EntityType } from '@tamalelibs/models/entity-type.model';
import { EntityService } from '@tamalelibs/services/entity.service';
import { AppState } from '../../../../redux';
import { debounceTime, filter, take, catchError } from 'rxjs/operators';
import { BriefObject } from '@tamalelibs/models/brief-object';
import { ContactTemplateControlValue } from '@tamalelibs/models/contact-template-control-value.model';
import { ChangeContactTemplateValueSet, ADD_JOB_TITLE, REMOVE_JOB_TITLE } from '@tamalelibs/redux/contact.actions';
import { validRelationshipTypeSelector } from '../../../../redux/reducers/valid-relationship-type.reducer';
import { Store, select } from '@ngrx/store';
import { EntityDialogOpenOptions, EntityDialogType, EntityBack } from '../../../entity-dialog/entity-dialog.model';
import { OpenDynamicComponentService } from '../../../../pages/home/home-open-dynamic-component';
import { ToastService } from '../../../../widgets/toast/toast.service';
import { NotificationStyles } from '../../../../widgets/notification/notification.model';
import { ControlId, JobTitleAction, ContactRelationshipType, RelationshipAction } from '../../contact-dialog.model';
import { of as observableOf } from 'rxjs';
import { EntityBrief } from '@tamalelibs/models/entity-brief.model';
import { ValidRelationshipType } from '@tamalelibs/models/valid-relationship-type.model';
import { ContactTemplateService } from '../../contact-template.service';
import { ContactTemplateOpenOptions } from '../../contact-dialog.model';
import { ContactTemplateType } from '../../contact-dialog.model';
import { Contact } from '@tamalelibs/models/contact.model';
import { NewRelationshipService } from '../../../new-relationship-dialog/new-relationship-dialog.service';
import { RelationshipDialogOpenOptions } from '../../../new-relationship-dialog/new-relationship-dialog.model';
import { Relationship } from '@tamalelibs/models/relationship.model';
import * as _lodash from 'lodash';
import { RelationshipType } from '@tamalelibs/models/relationship-type.model';
import { EntryService } from '@tamalelibs/services/entry.service';
import { contactNewAddedJobTitleSelector } from '@tamalelibs/redux/contact.reducer';
import { HomeViewModel } from '../../../../pages/home/home-view.model';

@Component({
    // tslint:disable-next-line: component-selector
    selector: 'ct-med',
    templateUrl: './ct-med.component.html',
    styleUrls: ['./ct-med.component.scss'],
    standalone: false
})
export class CtMedComponent implements OnInit, OnDestroy {

    @Input() config: any;
    @Input() valueChangeSubject: ReplaySubject<ContactTemplateControlValue>;
    @ViewChild('entitylist', { static: false }) entitylist;
    isFly = true;
    readOnly = false; // flag to disable auto complete
    selectedDictionary: any = {}; // selected items, using dictionary for quickly removing duplicates
    inputValue = ''; // input box text value
    suggestionItems: Array<any> = []; // suggestion items based on search
    dataItems: Array<any> = []; // sub set of dataItems
    selectedItems: Array<any> = []; // selected items, sub set of dataItems
    placeholder = '';
    hintContent = '';
    isMatchJobTitle = true;
    isShowCreateContact = false;
    isShowCreateEntity = false;
    isShowCreateRelationship = false;
    isHide = true;
    isAutoClose = false;

    private _relationshipType = ''; // to store the dropDown's relationship type's type: contact-all or contact
    private _validRelationshipTypes: Array<ValidRelationshipType>;
    private _filterHandler$: Subject<string> = new Subject();
    private _searchValue: string;
    private _entityTypes: Array<EntityType>;
    private _previousHeight = 30;
    private _page = 0;
    private _pageSize: number = AppConfig.searchingPageSize;
    private _source: any = {}; // mark data source configuration
    private _delimiter = ',';
    // watch another contraol's value, if it is not string, convert it to string, used for getting list according
    // a specific relationship of this vlue
    private _watchControlValue: any = [];
    private _destroySubscriptions: Array<Subscription> = [];
    private _searchId = 'searchId';
    constructor(
        private _entityService: EntityService,
        private _store: Store<AppState>,
        private _openDynamicComponentService: OpenDynamicComponentService,
        private _newRelationshipService: NewRelationshipService,
        private _toast: ToastService,
        private _contactTemplateService: ContactTemplateService,
        private _entryService: EntryService,
        private _homeViewModel: HomeViewModel,
    ) { }

    ngOnInit() {
        this._destroySubscriptions.push(
            // feedback for fly entity result
            this._openDynamicComponentService.feedbackSubject$.subscribe((feedback: EntityBack) => {
                if (feedback.controlId === this.config.controlID) {
                    this._destroySubscriptions.push(
                        this._entityService.getEntityListByIdsQuick([feedback.entityId], this._source, this._watchControlValue)
                            .subscribe(res => this._setFlyValue(res)));
                }
            }),

            this._newRelationshipService.feedbackSubject$.subscribe((feedback: Relationship) => {
                if (this.config.controlID === ControlId.JobTitle) {
                    const flyRelationship = {
                        name: feedback.relationshipType,
                        id: feedback.relationshipId
                    };
                    this.suggestionItems.push(flyRelationship);
                    this.selectedItems.push(flyRelationship);
                }
            }),
            this._store.pipe(
                select(validRelationshipTypeSelector),
                filter(res => res && res.length > 0),
            ).subscribe(res => {
                this._validRelationshipTypes = res;
                this._initData();
            }),

            this._filterHandler$.pipe(
                debounceTime(250),
            ).subscribe(filterStr => {
                if (filterStr && filterStr.length > 0) {
                    this._searching(filterStr);
                } else {
                    this._searching('');
                }
            }),

            this._store.pipe(
                select(contactNewAddedJobTitleSelector),
                filter(res => res && res.length > 0),
            ).subscribe(res => this._handleNewAddedJobTitle(res)),

            this._contactTemplateService.actionSubject$.subscribe(action => {
                // to make sure there is no dupulicate
                if (action.type === RelationshipAction.ChangeRelationshipType && this.config.groupId && (this.config.groupId === action.payload.groupId)) {
                    this._relationshipType = action.payload.relationshipType;
                    // Entity dropdown control should be blank out if user changes the relationship type
                    this.clearValue();
                    this._searching('');
                    this.isShowCreateEntity = false;
                    this.isShowCreateContact = false;
                }
            }),
        );

        if (this.config.staticDefaultValue) {
            // only edit, have this function
            this._destroySubscriptions.push(
                this._contactTemplateService.actionSubject$.subscribe(res => {
                    if (res.type === JobTitleAction.ClearJobTitile && res.payload === this.config.controlID) {
                        this.clearValue();
                    }
                }),
            );
            if (this.config.controlID === ControlId.JobTitle && this.config.staticDefaultValue.length > 0) {
                this.config.staticDefaultValue.forEach(element => {
                    if (element.id && element.id.indexOf('fakeId') > -1) {
                        this._store.dispatch({
                            type: ADD_JOB_TITLE,
                            payload: {
                                name: element.name,
                                id: element.id,
                            }
                        });
                    }
                });
            }
        }
        this._onSize(true);
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(item => item.unsubscribe());
    }

    handleFilter(value) {
        this._onSize();
        this._searchValue = value;
        this._filterHandler$.next(value);
    }

    medOnFocus(event: any) {
        this._onSize();
    }

    valueChange(tappedItem) {
        if (this.config.controlID === ControlId.JobTitle) {
            const searchIndex = tappedItem.findIndex(item => item.id === this._searchId);
            if (searchIndex > -1) {
                this._searchValue = tappedItem[searchIndex].name;
                delete tappedItem[searchIndex];
                const deleteIndex = this.suggestionItems.findIndex(item => item.id === this._searchId);
                delete this.suggestionItems[deleteIndex];
                this.createValidRelationshipByRestApi();
                return;
            }
            if (tappedItem.length >= 6) {
                this.isAutoClose = true;
            } else {
                this.isAutoClose = false;
            }
        }
        this._contactTemplateService.changeValueStatus = true;
        const originSelected = this.selectedDictionary;
        const originSelectedKeys = Object.keys(originSelected);
        this.selectedDictionary = {};
        tappedItem.forEach(ele => {
            this.selectedDictionary[ele.id] = ele.name;
        });
        // 1. For the remove:
        // 2. if user remove a job title, need to confirm if the job title is a fake job title
        if (originSelectedKeys.length > tappedItem.length) {
            const original = originSelectedKeys.find(origin => !tappedItem.find(target => target.id === origin) && origin.toString().indexOf('fakeId') > -1);
            if (original) {
                this._store.dispatch({
                    type: REMOVE_JOB_TITLE,
                    payload: {
                        name: originSelected[original],
                        id: original,
                    }
                });
            }
        }
        // 1. For the add:
        if (originSelectedKeys.length < tappedItem.length) {
            const targetIndex = tappedItem.findIndex(target => !originSelectedKeys.find(origin => target.id === origin) && target.id.toString().indexOf('fakeId') > -1);
            if (targetIndex > -1) {
                this._store.dispatch({
                    type: ADD_JOB_TITLE,
                    payload: {
                        name: tappedItem[targetIndex].name,
                        id: tappedItem[targetIndex].id,
                    }
                });
            }
        }
        if (this.config.controlID.indexOf(ControlId.AddRelationshipEntity) !== -1) {
            this._contactTemplateService.actionSubject$.next({
                type: RelationshipAction.ChangeRelationshipEntity
            });
        }
        this._changeValueSet(true);
        this._onSize();
    }

    clearValue() {
        this.inputValue = '';
        this.selectedItems = [];
        this.selectedDictionary = []; // need to remove this array, or the store will keep the original value
        this.entitylist.clearFilter();
        this.entitylist.toggle(false);
        this._changeValueSet(true);
    }

    /**
  * Create Entity
  */
    createEntity() {
        this.showAddEntityDialog();
    }
    /**
     * Create Contact
     */
    createContact() {
        this.showAddContactDialog();
    }

    onOpen(event: any): void {
        if (this.selectedItems.length >= 7 && this.config.controlID === ControlId.JobTitle) {
            event.preventDefault();
        }
    }

    showAddContactDialog() {
        let index;
        this._homeViewModel.increment();
        this._homeViewModel.getFlyIndex().pipe(take(1)).subscribe(res => index = res);
        const _contactDialogOpenOptions = new ContactTemplateOpenOptions();
        _contactDialogOpenOptions.openType = ContactTemplateType.NewContact;
        const contact = new Contact();
        contact.name = this._searchValue;
        _contactDialogOpenOptions.contact = contact;
        const entityBack = new EntityBack();
        _contactDialogOpenOptions.instanceId = index.toString();
        entityBack.controlId = this.config.controlID;
        entityBack.searchValue = this._searchValue;
        _contactDialogOpenOptions.entityBack = entityBack;
        _contactDialogOpenOptions.isFly = true;
        this._openDynamicComponentService.openContactDialog$.next(_contactDialogOpenOptions);
    }

    createValidRelationship() {
        const relationshipDialogOpenOptions = new RelationshipDialogOpenOptions();
        relationshipDialogOpenOptions.relationshiTyeName = this._searchValue;
        relationshipDialogOpenOptions.controlId = this.config.controlID;
        this._newRelationshipService.dialogOpen$.next(relationshipDialogOpenOptions);
    }

    createValidRelationshipByRestApi() {
        if (this.config.controlID === ControlId.JobTitle) {
            const invalidRelationshipTypeId = 'fakeId' + new Date().getTime();
            const flyRelationship = {
                id: invalidRelationshipTypeId,
                name: this._searchValue
            };
            this.suggestionItems = this.dataItems;
            this.selectedItems.push(flyRelationship);
            this.selectedDictionary[flyRelationship.id] = flyRelationship.name;

            // below is for create the job title
            this._store.dispatch({
                type: ADD_JOB_TITLE,
                payload: flyRelationship
            });

            this.entitylist.clearFilter();
            this.entitylist.toggle(false);
            this._changeValueSet(true);
        }
    }

    showAddEntityDialog() {
        let index;
        this._homeViewModel.increment();
        this._homeViewModel.getFlyIndex().pipe(take(1)).subscribe(res => index = res);
        const _entityDialogOpenOptions = new EntityDialogOpenOptions();
        _entityDialogOpenOptions.openType = EntityDialogType.NewEntity;
        _entityDialogOpenOptions.instanceId = index.toString();
        _entityDialogOpenOptions.searchValue = this._searchValue;
        _entityDialogOpenOptions.controlId = this.config.controlID;
        // set entityTypeId for fly entity-Dialog
        if (this.config.source && this.config.source.entityType && this.config.source.entityType.length > 0) {
            // make the right entity type be from source.entityType
            _entityDialogOpenOptions.entityType = this.config.source.entityType;
            const hasType = this.config.source.entityType.some(item => {
                return this._entityTypes.findIndex(abc => abc.id === item) > -1;
            });
            if (hasType) {
                this._openDynamicComponentService.feedbackSubject$.pipe(
                    take(1),
                ).subscribe((feedback: EntityBack) => {
                    if (feedback.entityId && feedback.controlId === this.config.controlID) {
                        this._entityService.getEntityListByIdsQuick([feedback.entityId])
                            .subscribe(res => {
                                this._setFlyValue(res);
                            });
                    }
                });
                this._openDynamicComponentService.openDialog$.next(_entityDialogOpenOptions);
            } else {
                this._toast.notify({
                    message: 'You do not have access to create entity of this type.',
                    style: NotificationStyles.Info
                });
            }
        } else {
            this._openDynamicComponentService.openDialog$.next(_entityDialogOpenOptions);
        }
    }

    private _handleNewAddedJobTitle(newAddedJobTitle) {
        if (this.config.controlID === 'JobTitle' && newAddedJobTitle && newAddedJobTitle.length > 0) {
            const tempValidRelationshipTypes = Object.assign([], this._validRelationshipTypes);
            newAddedJobTitle.forEach(element => {
                if (element.count > 0) {
                    tempValidRelationshipTypes.push({
                        id: element.id,
                        childEntityType: EntityType.CONTACT,
                        parentEntityType: EntityType.CONTACT,
                        relationshipType: {
                            id: element.id,
                            name: element.name,
                        },
                        reciprocalRelationshipType: null,
                    });
                }
                this._filterRelationship(tempValidRelationshipTypes);
            });
        }
    }

    private _initData() {
        if (this.config.controlID === ControlId.JobTitle) {
            this.placeholder = 'Create Job Title';
            this.isFly = true;
            this.isShowCreateRelationship = true;
            this._filterRelationship(this._validRelationshipTypes);
        } else if (this.config.controlID.indexOf(ControlId.AddRelationshipEntity) !== -1) {
            this.isFly = true;
            if (this._relationshipType === ContactRelationshipType.ContactAll) {
                this.isShowCreateEntity = false;
                this.isShowCreateContact = false;
            } else if (this._relationshipType === ContactRelationshipType.Contact) {
                this.isShowCreateEntity = false;
                // TODO: need to support create contact
                this.isShowCreateContact = false;
            }
        }
        if (this.config.staticDefaultValue) {
            this._initStaticDefaultValue();
        }
    }

    private _initStaticDefaultValue() {
        this.selectedDictionary = {};
        this.selectedItems = [];
        // init for relationship entities
        if (this.config.controlID.indexOf(ControlId.AddRelationshipEntity) !== -1) {
            // tslint:disable-next-line: forin
            for (const index in this.config.staticDefaultValue) {
                this.selectedDictionary[this.config.staticDefaultValue[index].childEntityId] = this.config.staticDefaultValue[index].childEntityName;
                this.selectedItems.push({
                    name: this.config.staticDefaultValue[index].childEntityName,
                    id: this.config.staticDefaultValue[index].childEntityId
                });
            }
            // because every node in med control will have the same node type, we just need to get the first value
            this._relationshipType = this.config.staticDefaultValue[0].nodeType;
            this.readOnly = !this.config.staticDefaultValue[0].isNodeEditable;
            this._searching('');
        } else if (this.config.controlID === ControlId.JobTitle) {
            this.config.staticDefaultValue.forEach(res => {
                if (!res.id && res.name) {
                    res.id = 'fakeId' + new Date().getTime();
                }
            });
            this.selectedItems = this.config.staticDefaultValue;
            this.selectedItems.forEach(item => {
                this.selectedDictionary[item.id] = item.name;
            });
            this._changeValueSet(true);
            this._searching('');
        }
        this._changeValueSet(false);
    }

    private _filterRelationship(res) {
        this.suggestionItems = [];
        res.forEach(element => {
            if (element.childEntityType.id === EntityType.CONTACT.id &&
                element.parentEntityType.id === EntityType.CONTACT.id) {
                this.suggestionItems.push({
                    'id': element.relationshipType.id,
                    'name': element.relationshipType.name
                });
            }
        });
        if (this.suggestionItems.length !== 0) {
            this.suggestionItems = _lodash.sortBy(this.suggestionItems, item => item.name);
        }
        this.dataItems = this.suggestionItems;
    }

    private _searching(text) {
        if (this.config.controlID === ControlId.JobTitle) {
            this.suggestionItems = this.dataItems.filter((content) => content.name.toLowerCase().indexOf(text.toLowerCase()) !== -1);
            const index = this.dataItems.findIndex((content) => content.name.toLowerCase() === text.toLowerCase());
            if (index < 0 && text) {
                const searchItem = { 'name': text, 'id': this._searchId };
                this.suggestionItems.push(searchItem);
            }
        } else {
            this._page = 0;
            if (this.entitylist) {
                this.entitylist.loading = true;
            }
            if (this._relationshipType === RelationshipType.IS_IN_TEAM.name) {
                this._destroySubscriptions.push(
                    this._entryService.getTeamsgetByParams(++this._page, this._pageSize, text, true).
                        pipe(take(1),
                            catchError((e) => {
                                return observableOf();
                            })).subscribe(res => {
                                this.entitylist.loading = false;
                                this.suggestionItems = [];
                                // in relationship part, need to support filter by name and shortname
                                res['team-list'].filter((content) =>
                                    content['long-name'].toLowerCase().indexOf(text.toLowerCase()) !== -1 ||
                                    content['short-name'].toLowerCase().indexOf(text.toLowerCase()) !== -1).forEach(element => {
                                        this.suggestionItems.push(EntityBrief.parse(element));
                                    });
                                this.dataItems = this.suggestionItems;
                                if (this.entitylist) {
                                    this.entitylist.loading = false;
                                }
                            }));
            } else {
                if (this._relationshipType === ContactRelationshipType.Contact) {
                    this._source.entityType = EntityType.CONTACT.id;
                } else {
                    this._source.entityType = '';
                }
                this._destroySubscriptions.push(
                    this._entityService.getEntityListBySearchTextQuick(++this._page, this._pageSize, text, this._source).
                        pipe(take(1),
                            catchError((e) => {
                                return observableOf();
                            })).subscribe(res => {
                                this.entitylist.loading = false;
                                this.dataItems = this._searchingDone(res);
                                // in relationship part, need to support filter by name and shortname
                                this.suggestionItems = this.dataItems.filter((content) =>
                                    content.name.toLowerCase().indexOf(text.toLowerCase()) !== -1 ||
                                    content.shortName.toLowerCase().indexOf(text.toLowerCase()) !== -1);
                                // For relationship part: need to get the match entity or contact
                                if (this._relationshipType === ContactRelationshipType.ContactAll) {
                                    this.suggestionItems = this.suggestionItems.filter(item => item.type.id !== EntityType.CONTACT.id &&
                                        item.type.id !== EntityType.USER.id);
                                    this.isShowCreateEntity = true;
                                    this.isShowCreateContact = false;
                                } else if (this._relationshipType === ContactRelationshipType.Contact) {
                                    this.suggestionItems = this.suggestionItems.filter(item => item.type.id === EntityType.CONTACT.id);
                                    this.isShowCreateContact = true;
                                    this.isShowCreateEntity = false;
                                }
                                if (this.entitylist) {
                                    this.entitylist.loading = false;
                                }
                            }));
            }
        }
    }

    private _searchingDone(entityList): Array<EntityBrief> {
        return this._entityService.mapEntityBriefList(entityList);
    }

    private _setFlyValue(entityList) {
        if (entityList) {
            const item = this._entityService.mapEntityBriefList(entityList)[0];
            if (item) {
                this.selectedItems.push(item);
                this.suggestionItems.push(this.selectedItems);
                this.selectedDictionary[item.id] = item.name;
                this._changeValueSet(true);
            }
        }
    }

    private _changeValueSet(isFromUser: boolean) {
        const valueSet = [];
        const nameSet = [];
        const actualValueSet = new Array<BriefObject>();
        // tslint:disable-next-line: forin
        for (const id in this.selectedDictionary) {
            valueSet.push(id);
            const val = this.selectedDictionary[id];
            nameSet.push(val);
            actualValueSet.push(new BriefObject(id, val));
        }
        const value = new ContactTemplateControlValue(valueSet.join(this._delimiter), nameSet.join(this._delimiter), this.config);
        if (this.config.controlID === ControlId.JobTitle && isFromUser) {
            // no matter edit or create, will have change job title function
            this._contactTemplateService.actionSubject$.next({
                type: JobTitleAction.ChangeJobTitle,
                payload: actualValueSet,
                prefixId: this.config.prefixId
            });
        }

        if (this.valueChangeSubject) {
            this.valueChangeSubject.next(value);
        }
        this._store.dispatch(
            new ChangeContactTemplateValueSet(
                value,
                'prefix',
                this.config.prefixId
            )
        );
    }

    /**
    *
    *resize the dropdown height
    * @private
    * @param {boolean} [isFirstEdit=false]
    * @memberof CtAliasComponent
    */
    private _onSize(isFirstEdit = false) {
        if (!isFirstEdit || !this.config.newHeight) {
            setTimeout(() => {
                const inpuHeight = this.entitylist.hostElement.clientHeight;
                if (inpuHeight !== this._previousHeight) {
                    const paddingInput = inpuHeight - this._previousHeight;
                    this.config['paddingInput'] = paddingInput;
                    this.config['newHeight'] = inpuHeight;
                    this._previousHeight = inpuHeight;
                    this._changeValueSet(true);
                } else {
                    this.config['paddingInput'] = 0;
                }
            }, 10);
        } else {
            this.config['paddingInput'] = this.config.newHeight - this._previousHeight;
            this._previousHeight = this.config.newHeight;
            this._changeValueSet(true);
        }
    }
}
